import {
  acceptSupportInvitation,
  previewSupportInvitation,
} from 'api/services/accessControlOperationsService'
import useAuthService from 'api/services/authService'
import { removeToken } from 'api/utils/AuthManager'
import validatePassword from 'api/utils/passwordStrength'
import {
  clearPendingApplyListingId,
  getPendingApplyListingId,
} from 'api/utils/pendingApplyListing'
import { markListingOwnerIntent } from 'api/utils/pendingCreateAdFlow'
import { validateEmail } from 'api/utils/validateEmail'
import AuthPageShell from 'components/auth/AuthPageShell'
import { EmailInput } from 'components/common/field'
import PasswordInput from 'components/common/field/Password'
import PasswordStrengthHint from 'components/common/field/PasswordStrengthHint'
import UnderLink from 'components/common/UnderLink'
import { WizardStepChangeEventHandler } from 'components/types'
import { Location } from 'newTypes/location'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FieldChangeEventAnswer } from 'types/general'

import { i18n } from '../i18n'
import { getBackgroundPathFromState } from '../utils/authNavigation'
import { getFriendlyRegisterErrorMessage } from '../utils/loginErrorMessages'

const RegisterUserPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const inviteToken = useMemo(
    () => (searchParams.get('invite') || '').trim(),
    [searchParams]
  )
  const { register } = useAuthService()
  const { refresh: refreshPermissions } = usePermissions()

  const title = inviteToken
    ? i18n.translate('auth.supportInviteRegisterTitle')
    : i18n.translate('auth.registerTitle')
  const subtitle = inviteToken
    ? i18n.translate('auth.supportInviteRegisterSubtitle')
    : i18n.translate('auth.registerSubtitle')
  const labelEmail = i18n.translate('common.email')
  const labelPassword = i18n.translate('common.password')
  const labelConfirmPassword = i18n.translate('auth.confirmPassword')
  const buttonRegister = i18n.translate('auth.registerButton')
  const alreadyHaveAccount = i18n.translate('auth.alreadyHaveAccount')
  const logInLabel = i18n.translate('auth.logIn')

  const [loading, setLoading] = useState(false)
  const [inviteLockedEmail, setInviteLockedEmail] = useState(false)
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [fieldValidity, setFieldValidity] = useState({
    email: false,
    password: false,
    confirmPassword: false,
  })
  const [confirmTouched, setConfirmTouched] = useState(false)

  const dismiss = () => {
    const pending = getPendingApplyListingId()
    clearPendingApplyListingId()
    if (pending) {
      navigate(`/listing/${pending}`)
      return
    }
    navigate(getBackgroundPathFromState(location.state))
  }

  const authLinkState = {
    from: (location.state as { from?: unknown } | null)?.from,
    backgroundLocation: (
      location.state as { backgroundLocation?: unknown } | null
    )?.backgroundLocation,
    applyListingId: (
      location.state as { applyListingId?: string } | null
    )?.applyListingId,
    resumeCreateAd: (
      location.state as { resumeCreateAd?: boolean } | null
    )?.resumeCreateAd,
  }

  const handleChange = (
    e:
      | ChangeEvent<HTMLInputElement>
      | WizardStepChangeEventHandler
      | FieldChangeEventAnswer
      | Location
  ) => {
    if ('target' in e) {
      const { name, value } = e.target
      if (name === 'email' && inviteLockedEmail) return
      setForm(prev => ({ ...prev, [name]: value }))
      if (name === 'confirmPassword') {
        setFieldValidity(prev => ({
          ...prev,
          confirmPassword: value === form.password && value.length > 0,
        }))
      } else if (name === 'password') {
        setFieldValidity(prev => ({
          ...prev,
          confirmPassword:
            form.confirmPassword === value && form.confirmPassword.length > 0,
        }))
      }
    } else if ('name' in e && 'value' in e && typeof e.value === 'string') {
      if (e.name === 'email' && inviteLockedEmail) return
      setForm(prev => ({ ...prev, [e.name]: e.value }))
    }
  }

  const handleFieldValidation = (name: string, isValid: boolean) => {
    setFieldValidity(prev => ({ ...prev, [name]: isValid }))
  }

  const passwordsMatch =
    form.password === form.confirmPassword && form.confirmPassword.length > 0
  const isFormValid =
    fieldValidity.email && fieldValidity.password && passwordsMatch

  const claimSupportInvite = async () => {
    if (!inviteToken) return false
    try {
      await acceptSupportInvitation(inviteToken)
      await refreshPermissions()
      return true
    } catch {
      // me-permissions may still claim by email on next load
      return false
    }
  }

  const handleRegister = async () => {
    const email = form.email.trim().toLowerCase()
    if (validateEmail(email, 'boolean') !== true) {
      toast.error(String(i18n.translate('auth.invalidEmailFormat')))
      return
    }
    if (!validatePassword(form.password)) {
      toast.error(String(i18n.translate('auth.passwordWeak')))
      return
    }
    if (form.password !== form.confirmPassword) {
      toast.error(
        i18n.translate('auth.errorPasswordMismatch') ||
          'Passwords do not match.'
      )
      return
    }
    const resumeCreateAd = Boolean(
      (location.state as { resumeCreateAd?: boolean } | null)?.resumeCreateAd
    )
    // Mark before Firebase register so the first /me-permissions bootstraps owner.
    if (resumeCreateAd) {
      markListingOwnerIntent()
    }
    setLoading(true)
    try {
      await register(
        email,
        form.password,
        resumeCreateAd ? { asOwner: true } : undefined
      )
      if (resumeCreateAd) {
        await refreshPermissions({ intent: 'list_property' })
      }
      const becameSupport = await claimSupportInvite()
      toast.success(
        becameSupport
          ? String(i18n.translate('auth.supportInviteAccepted'))
          : i18n.translate('auth.registerSuccess') ||
              'Account created. You are now logged in.'
      )
      if (becameSupport || inviteToken) {
        navigate('/support-console', { replace: true })
        return
      }
      if (resumeCreateAd) {
        // Return to the wizard (draft restores steps). Owner publish uses
        // /create-ad/register-owner — never resumeCreateAd without a payload.
        navigate('/create-ad', { replace: true })
        return
      }
      const pendingListingId = getPendingApplyListingId()
      if (pendingListingId) {
        clearPendingApplyListingId()
        navigate(`/listing/${pendingListingId}`, {
          state: { resumeInterest: true },
          replace: true,
        })
        return
      }
      navigate('/post-register-choice')
    } catch (err) {
      toast.error(getFriendlyRegisterErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (loading || !isFormValid) return
    void handleRegister()
  }

  useEffect(() => {
    // Never wipe the session when returning from create-ad owner registration.
    const resumeCreateAd = Boolean(
      (location.state as { resumeCreateAd?: boolean } | null)?.resumeCreateAd
    )
    if (resumeCreateAd) return
    removeToken()
  }, [location.state])

  useEffect(() => {
    const emailFromState = (location.state as { email?: unknown } | null)?.email
    if (typeof emailFromState === 'string' && emailFromState.trim() !== '') {
      const normalized = emailFromState.trim().toLowerCase()
      setForm(prev => ({ ...prev, email: normalized }))
      setFieldValidity(prev => ({
        ...prev,
        email: validateEmail(normalized, 'boolean') === true,
      }))
    }
  }, [location.state])

  useEffect(() => {
    if (!inviteToken) return
    let cancelled = false
    ;(async () => {
      try {
        const preview = await previewSupportInvitation(inviteToken)
        if (cancelled) return
        const email = preview.email.trim().toLowerCase()
        setForm(prev => ({ ...prev, email }))
        setFieldValidity(prev => ({
          ...prev,
          email: validateEmail(email, 'boolean') === true,
        }))
        setInviteLockedEmail(true)
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : String(i18n.translate('auth.supportInviteInvalid'))
          )
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [inviteToken])

  useEffect(() => {
    setFieldValidity(prev => ({
      ...prev,
      confirmPassword:
        form.confirmPassword === form.password &&
        form.confirmPassword.length > 0,
    }))
  }, [form.password, form.confirmPassword])

  return (
    <AuthPageShell title={title} subtitle={subtitle}>
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 [&>div]:!my-0"
      >
        <EmailInput
          name="email"
          label={labelEmail}
          value={form.email}
          onChange={handleChange}
          setIsValid={isValid => handleFieldValidation('email', isValid)}
          isFirstInput
          readOnly={inviteLockedEmail}
        />
        <PasswordInput
          name="password"
          label={labelPassword}
          value={form.password}
          onChange={handleChange}
          setIsValid={isValid => handleFieldValidation('password', isValid)}
          isValidationRequired
        />
        <PasswordStrengthHint password={form.password} />
        <PasswordInput
          name="confirmPassword"
          label={labelConfirmPassword}
          value={form.confirmPassword}
          onChange={handleChange}
          setIsValid={isValid =>
            handleFieldValidation('confirmPassword', isValid && passwordsMatch)
          }
          isValidationRequired={false}
          onBlurExtra={() => {
            setConfirmTouched(true)
            handleFieldValidation('confirmPassword', passwordsMatch)
          }}
        />
        {confirmTouched &&
        form.confirmPassword.length > 0 &&
        !passwordsMatch ? (
          <p role="alert" className="text-xs text-red-600">
            {String(i18n.translate('auth.errorPasswordMismatch'))}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={!isFormValid || loading}
          className="mt-1 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-imovPrimary py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark disabled:cursor-not-allowed disabled:bg-imovPrimary/40"
        >
          {loading ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <>
              {buttonRegister}
              <span aria-hidden>→</span>
            </>
          )}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        <span>{alreadyHaveAccount} </span>
        <UnderLink
          label={logInLabel}
          redirectTo={inviteToken ? `/auth?invite=${encodeURIComponent(inviteToken)}` : '/auth'}
          state={authLinkState}
          isUnderline
          className="inline font-semibold text-imovPrimary hover:text-imovPrimaryDark"
        />
      </p>

      <button
        type="button"
        onClick={dismiss}
        className="mt-3 w-full py-2 text-center text-sm text-gray-500 transition hover:text-imovSecondary"
      >
        {i18n.translate('common.cancel')}
      </button>
    </AuthPageShell>
  )
}

export default RegisterUserPage
